import { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import {
  productConfig, validateLicenceFn, getMyLicenceFn, entitles, entitlesAnyTool, normaliseScope,
} from './licenceApi';

/* Resolves licence state for a route product, shared by the Roadmap and Planner.
 *
 * Order: paid author (synthetic bundle licence) → signed-in getMyLicence auto-
 * unlock → cached key+email (re-validated) → gate. A cached licence that no
 * longer entitles this product is cleared. Returns state + handlers; the caller
 * renders the gate / pricing / tool.
 */
export default function useLicence(product = 'roadmap', { authorSubscription, anyTool = false } = {}) {
  // ONE PLAN: `anyTool` means any active scope entitles this tool. Used by the
  // Story Bible, which is included with every plan rather than sold separately.
  const entitledBy = (scope) => (anyTool ? entitlesAnyTool(scope) : entitles(scope, product));
  const cfg = productConfig(product);
  const cacheKey = cfg.cacheKey;

  const [licenceData, setLicenceData] = useState(null);
  const [expired, setExpired]         = useState(false);
  const [checking, setChecking]       = useState(true);
  const [authUser, setAuthUser]       = useState(null);

  // Paid authors get a synthetic bundle licence (SUB- key entitles both tools).
  const isFreeAuthor  = authorSubscription && authorSubscription.tier === 'free';
  const isPaidOrAdmin = authorSubscription && (authorSubscription.tier === 'pro' || authorSubscription.tier === 'publisher' || authorSubscription.tier === 'superadmin');

  // Track auth state for the sign-out control (separate from the one-shot unlock).
  useEffect(() => onAuthStateChanged(auth, setAuthUser), []);

  const setUnlocked = (data, exp) => { setLicenceData(data); setExpired(!!exp); };

  const handleSignOut = async () => {
    // Clear local licence/session state ONLY after Firebase sign-out actually
    // succeeds. Previously a swallowed failure here cleared the local unlock while
    // leaving the user Firebase-authenticated — "looks logged out, still signed in".
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Sign-out failed:', err);
      if (typeof window !== 'undefined') {
        window.alert('Sign-out failed — you are still signed in. Please check your connection and try again.');
      }
      return; // keep local state so the UI matches reality (still signed in)
    }
    localStorage.removeItem(cacheKey);
    setLicenceData(null);
    setExpired(false);
  };

  useEffect(() => {
    if (isPaidOrAdmin) {
      setLicenceData({
        key: `SUB-${authorSubscription.authorId || 'author'}`,
        email: authorSubscription.email || 'author@narratoria.com',
        scope: 'bundle',
      });
      setChecking(false);
      return;
    }

    let cancelled = false;
    let handled = false;

    // Signed-out web users: a cached key+email, re-validated, else the gate.
    const fromCacheOrGate = async () => {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) { if (!cancelled) setChecking(false); return; }
      let parsed;
      try { parsed = JSON.parse(raw); } catch { localStorage.removeItem(cacheKey); if (!cancelled) setChecking(false); return; }
      try {
        const res = await validateLicenceFn({ key: parsed.key, email: parsed.email });
        if (cancelled) return;
        const scope = normaliseScope(res.data?.licence?.scope);
        if (!res.data?.valid || !entitledBy(scope)) {
          localStorage.removeItem(cacheKey);
        } else {
          const merged = {
            ...parsed,
            term: res.data?.licence?.term || parsed.term || null,
            scope,
            expiresAtMs: res.data?.licence?.expiresAtMs ?? parsed.expiresAtMs ?? null,
          };
          localStorage.setItem(cacheKey, JSON.stringify(merged));
          setLicenceData(merged); setExpired(!!res.data?.expired);
        }
      } catch { if (!cancelled) setLicenceData(parsed); } // allow offline
      finally { if (!cancelled) setChecking(false); }
    };

    // A signed-in user auto-unlocks via their claimed / verified-email licence for
    // this product — no code needed. Signed-out users fall through to cache/gate.
    const proceed = async (user) => {
      if (handled || cancelled) return;
      handled = true;
      if (user) {
        try {
          const res = await getMyLicenceFn({ product: cfg.product, ...(anyTool ? { anyTool: true } : {}) });
          if (cancelled) return;
          const lic = res.data;
          if (lic?.found && lic.licence?.key) {
            const data = {
              key: lic.licence.key,
              email: lic.licence.email,
              term: lic.licence.term || null,
              scope: normaliseScope(lic.licence.scope),
              expiresAtMs: lic.licence.expiresAtMs ?? null,
            };
            localStorage.setItem(cacheKey, JSON.stringify(data));
            setLicenceData(data);
            setExpired(!!lic.expired);
            setChecking(false);
            return;
          }
        } catch { /* fall through to cache / gate */ }
      }
      if (!cancelled) fromCacheOrGate();
    };

    const unsub = onAuthStateChanged(auth, proceed);
    return () => { cancelled = true; unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaidOrAdmin, authorSubscription, cacheKey, product, cfg.product, anyTool]);

  return {
    checking, licenceData, expired, authUser,
    isFreeAuthor, isPaidOrAdmin,
    setUnlocked, setLicenceData, setExpired, handleSignOut,
  };
}
